import { Logger, LogEnricher, LogTransport, EnrichedLogEntry, BreadcrumbType } from './types';
import { LogLevel, shouldLog } from './levels';
import { applyEnrichers, TraceEnricher } from './enrichers';
import { BreadcrumbCollector } from './breadcrumbs';
import { TraceContext } from './trace-context';

export class LoggerImpl implements Logger {
  constructor(
    private readonly minLevel: LogLevel,
    private readonly enrichers: LogEnricher[],
    private readonly transports: LogTransport[],
    private readonly breadcrumbs: BreadcrumbCollector,
    private readonly traceContext: TraceContext,
  ) {}

  private log(level: LogLevel, message: string, error?: Error, context?: Record<string, unknown>): void {
    if (!shouldLog(level, this.minLevel)) return;

    const enriched = applyEnrichers(
      { level, message, error, context },
      this.enrichers,
    ) as unknown as EnrichedLogEntry;

    enriched.timestamp = new Date().toISOString();

    if (level === LogLevel.ERROR) {
      enriched.breadcrumbs = this.breadcrumbs.getAll();
    }

    for (const transport of this.transports) {
      transport.send(enriched);
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, undefined, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, undefined, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, undefined, context);
  }

  error(message: string, error?: Error, context?: Record<string, unknown>): void {
    this.log(LogLevel.ERROR, message, error, context);
  }

  flush(): Promise<void> {
    return Promise.resolve();
  }

  withOperation(name: string): Logger {
    const childTrace = this.traceContext.startOperation(name);
    const enrichersWithTrace = this.enrichers.map((e) =>
      e instanceof TraceEnricher ? new TraceEnricher(childTrace) : e,
    );
    return new LoggerImpl(this.minLevel, enrichersWithTrace, this.transports, this.breadcrumbs, childTrace);
  }

  addBreadcrumb(type: BreadcrumbType, message: string, data?: Record<string, unknown>): void {
    this.breadcrumbs.add(type, message, data);
  }
}
